import AsyncStorage from "@react-native-async-storage/async-storage";
import Constants from "expo-constants";
import { getApp, getApps, initializeApp } from "firebase/app";
import * as FirebaseAuth from "firebase/auth";
import type { Persistence } from "firebase/auth";
import { initializeFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { Platform } from "react-native";

const firebaseConfig = {
  apiKey: process.env.EXPO_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID,
  appId: process.env.EXPO_PUBLIC_FIREBASE_APP_ID
};

// EXPO_PUBLIC_APP_MODE=dev keeps the app in design mode (public synthetic
// fixture catalog, no auth gate) even when Firebase client config is present.
const forcedDevMode = process.env.EXPO_PUBLIC_APP_MODE === "dev";

export const hasFirebaseClientConfig =
  !forcedDevMode &&
  Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.authDomain &&
      firebaseConfig.projectId &&
      firebaseConfig.appId
  );

const firebaseApp = hasFirebaseClientConfig
  ? getApps().length > 0
    ? getApp()
    : initializeApp(firebaseConfig)
  : null;

function getFirebaseAuth() {
  if (!firebaseApp) return null;
  if (Platform.OS === "web") return FirebaseAuth.getAuth(firebaseApp);

  try {
    // Firebase 12's React Native runtime exports this from `firebase/auth`,
    // while the wrapper package's generic declaration omits the RN-only
    // member even though @firebase/auth's RN declaration includes it.
    const getReactNativePersistence = (
      FirebaseAuth as typeof FirebaseAuth & {
        getReactNativePersistence: (storage: typeof AsyncStorage) => Persistence;
      }
    ).getReactNativePersistence;
    return FirebaseAuth.initializeAuth(firebaseApp, {
      persistence: getReactNativePersistence(AsyncStorage)
    });
  } catch (error) {
    if ((error as { code?: string }).code !== "auth/already-initialized") throw error;
    return FirebaseAuth.getAuth(firebaseApp);
  }
}

export const firebaseAuth = getFirebaseAuth();
// Long-polling autodetect keeps the catalog loading when ad blockers kill the
// default WebChannel streaming endpoint (ERR_BLOCKED_BY_CLIENT).
export const firestore = firebaseApp
  ? initializeFirestore(firebaseApp, { experimentalAutoDetectLongPolling: true })
  : null;
export const functions = firebaseApp
  ? getFunctions(firebaseApp, process.env.EXPO_PUBLIC_FUNCTIONS_REGION ?? "us-central1")
  : null;

function localFunctionsEmulatorOrigin(): string | null {
  // Billing is deliberately fail-closed in local development: dev builds use
  // Stripe test mode through the Functions emulator, while production builds
  // can only use the deployed live-mode functions. Never infer payment mode
  // from a request origin inside the backend.
  if (!__DEV__ || !firebaseConfig.projectId) return null;

  const configured = process.env.EXPO_PUBLIC_FUNCTIONS_EMULATOR_HOST?.trim();
  const configuredUrl = configured
    ? new URL(configured.includes("://") ? configured : `http://${configured}`)
    : null;
  const expoHostUri = Constants.expoConfig?.hostUri;
  const expoHost = expoHostUri
    ? new URL(expoHostUri.includes("://") ? expoHostUri : `http://${expoHostUri}`).hostname
    : null;
  const host =
    configuredUrl?.hostname ||
    (Platform.OS === "web" && typeof window !== "undefined" ? window.location.hostname : null) ||
    expoHost ||
    "127.0.0.1";
  const port = configuredUrl?.port || "5001";
  const region = process.env.EXPO_PUBLIC_FUNCTIONS_REGION ?? "us-central1";
  return `http://${host}:${port}/${firebaseConfig.projectId}/${region}`;
}

export const billingFunctionsEmulatorOrigin = localFunctionsEmulatorOrigin();
/**
 * Only location-aware calls use the Cloudflare edge. All other callable
 * functions continue to use Firebase's regional endpoint directly.
 */
export const edgeFunctions = firebaseApp
  ? getFunctions(
      firebaseApp,
      process.env.EXPO_PUBLIC_AUTH_EDGE_URL ?? "https://auth.recitalmaster.com"
    )
  : null;
